/**
 * Authentication Context
 * Provides authentication state and methods throughout the app
 * Enhanced with role and permission extraction from Cognito groups
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { signIn, signOut, getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import { setAuthToken } from '../config/api';
import { ROLES, ROLE_PERMISSIONS } from '../shared/constants/roles';
import { useStore } from '../store/store';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Extract user role from Cognito groups
 * Cognito groups are available in the ID token claims
 */
const extractUserRole = (token) => {
  try {
    // Decode JWT token (simple base64 decode, not full verification)
    const payload = JSON.parse(atob(token.split('.')[1]));
    
    // Check for Cognito groups in token claims
    // Groups can be in 'cognito:groups' or custom claims
    const groups = payload['cognito:groups'] || payload.groups || [];
    
    // Map Cognito groups to application roles
    // Default to 'viewer' if no groups found
    if (groups.length === 0) {
      return ROLES.VIEWER; // Default role
    }
    
    // Find the highest role from groups
    // Groups should match role names: 'super-admin', 'admin', 'manager', 'operator', 'viewer'
    const roleMap = {
      'super-admin': ROLES.SUPER_ADMIN,
      'admin': ROLES.ADMIN,
      'manager': ROLES.MANAGER,
      'operator': ROLES.OPERATOR,
      'viewer': ROLES.VIEWER,
    };
    
    // Return the highest role found
    for (const group of groups) {
      const role = roleMap[group.toLowerCase()];
      if (role) {
        return role;
      }
    }
    
    return ROLES.VIEWER; // Default fallback
  } catch (error) {
    console.error('Error extracting user role:', error);
    return ROLES.VIEWER; // Default fallback
  }
};

/**
 * Get permissions for a role
 */
const getRolePermissions = (role) => {
  return ROLE_PERMISSIONS[role] || [];
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState(null);
  const [userRole, setUserRole] = useState(ROLES.VIEWER);
  const [userPermissions, setUserPermissions] = useState([]);
  
  // Zustand store actions
  const setUserRoles = useStore((state) => state.setUserRoles);
  const setUserPermissionsStore = useStore((state) => state.setUserPermissions);

  // Check authentication status on mount
  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      setLoading(true);
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();
      
      const idToken = session.tokens?.idToken?.toString();
      
      if (idToken) {
        // Extract role from token
        const role = extractUserRole(idToken);
        const permissions = getRolePermissions(role);
        
        setUser(currentUser);
        setIsAuthenticated(true);
        setToken(idToken);
        setUserRole(role);
        setUserPermissions(permissions);
        
        // Update Zustand store
        setUserRoles([role]);
        setUserPermissionsStore(permissions);
        
        setAuthToken(idToken); // Set token in API client
        
        console.log('User authenticated:', {
          username: currentUser.username,
          role: role,
          permissions: permissions.length,
        });
      } else {
        throw new Error('No token found');
      }
    } catch (error) {
      console.log('User not authenticated:', error);
      setUser(null);
      setIsAuthenticated(false);
      setToken(null);
      setUserRole(ROLES.VIEWER);
      setUserPermissions([]);
      setUserRoles([]);
      setUserPermissionsStore([]);
      setAuthToken(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    setLoading(true);
    try {
      console.log('🔐 Attempting login with:', { username: username.trim(), hasPassword: !!password });
      
      // Try signIn with username/password
      const result = await signIn({ 
        username: username.trim(), 
        password: password 
      });
      
      console.log('📋 SignIn result:', result);
      console.log('📋 SignIn result type:', typeof result);
      console.log('📋 SignIn result keys:', result ? Object.keys(result) : 'result is null/undefined');
      
      // Handle different result structures
      if (!result) {
        console.log('⚠️ SignIn returned null/undefined - verifying session...');
        // signIn might succeed but return undefined - verify by checking session directly
        try {
          // Check if we have a valid session
          const session = await fetchAuthSession();
          const idToken = session.tokens?.idToken?.toString();
          
          if (idToken) {
            // Session exists, get user and update auth status
            const currentUser = await getCurrentUser();
            console.log('✅ User authenticated (verified via session after undefined result)');
            await checkAuthStatus(); // Update all auth state
            return { success: true, nextStep: null };
          } else {
            console.error('❌ No token found in session');
            return { 
              success: false, 
              error: 'Authentication failed. Please check your credentials.' 
            };
          }
        } catch (e) {
          console.error('❌ Could not verify session:', e);
          return { 
            success: false, 
            error: 'Authentication failed. Please check your credentials.' 
          };
        }
      }
      
      // Check if signed in
      if (result.isSignedIn === true) {
        console.log('✅ User signed in successfully');
        await checkAuthStatus(); // Re-check status to get user, token, and role
        return { success: true, nextStep: null };
      }
      
      // Check for next step
      if (result.nextStep) {
        console.log('⏭️ Next step required:', result.nextStep);
        
        // Handle different next steps
        const signInStep = result.nextStep.signInStep;
        if (signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
          return { 
            success: false, 
            nextStep: result.nextStep,
            error: 'Password change required. Please set a new password.' 
          };
        } else if (signInStep === 'CONFIRM_SIGN_IN_WITH_SMS_MFA_CODE' || 
                   signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_MFA_CODE') {
          return { 
            success: false, 
            nextStep: result.nextStep,
            error: 'MFA code required.' 
          };
        } else {
          console.log('⚠️ Unknown next step:', signInStep);
          return { 
            success: false, 
            nextStep: result.nextStep,
            error: 'Additional authentication step required.' 
          };
        }
      }
      
      // If we get here, signIn completed but no clear success/failure
      // Try to verify by checking session directly
      console.log('🔍 SignIn completed, verifying session...');
      try {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();
        
        if (idToken) {
          // Session exists, update auth status
          const currentUser = await getCurrentUser();
          console.log('✅ User authenticated (verified via session)');
          await checkAuthStatus(); // Update all auth state
          return { success: true, nextStep: null };
        } else {
          console.error('❌ No token found in session');
        }
      } catch (e) {
        console.error('❌ Could not verify session:', e);
      }
      
      console.warn('⚠️ SignIn returned unexpected result:', result);
      return { 
        success: false, 
        error: 'Authentication failed. Please check your credentials.' 
      };
    } catch (error) {
      console.error('Login error details:', {
        name: error.name,
        message: error.message,
        cause: error.cause,
        stack: error.stack
      });
      
      // Provide more specific error messages
      let errorMessage = error.message || 'An unexpected error occurred during login.';
      
      if (error.name === 'NotAuthorizedException') {
        errorMessage = 'Invalid username or password.';
      } else if (error.name === 'UserNotFoundException') {
        errorMessage = 'User not found. Please check your email.';
      } else if (error.name === 'UserNotConfirmedException') {
        errorMessage = 'User account not confirmed. Please verify your email.';
      } else if (error.name === 'TooManyRequestsException') {
        errorMessage = 'Too many login attempts. Please try again later.';
      } else if (error.message?.includes('Network')) {
        errorMessage = 'Network error. Please check your connection.';
      }
      
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    setLoading(true);
    try {
      await signOut();
      await checkAuthStatus(); // Clear user, token, and role
      console.log('User logged out');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getAuthToken = async () => {
    try {
      const { tokens } = await fetchAuthSession();
      return tokens.idToken?.toString();
    } catch (error) {
      console.error('Error fetching auth token:', error);
      return null;
    }
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    token,
    userRole,
    userPermissions,
    login,
    logout,
    getAuthToken,
    checkAuthStatus,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
